import React from 'react';

// "Ledger & Seal" family, Agentic Readiness edition -- shares the shell
// used by RFPResponseOnePager and DynamicPricingOnePager. No title/video
// here -- heroBanner: true handles that (see useCases.js).
const PALETTE = {
  blueLight: '#60a5fa',
  blue: '#1d4ed8',
  indigo: '#4f46e5',
  violet: '#6d28d9',
  pink: '#9d174d',
};

// Ends on violet, not dark pink like RFP's chain.
const stampGradient = `linear-gradient(120deg, ${PALETTE.blueLight} 0%, ${PALETTE.blue} 35%, ${PALETTE.indigo} 65%, ${PALETTE.violet} 100%)`;

// Separate gradient just for the ledger numbers, kept distinct from PALETTE.
const LEDGER_COLORS = ['#60a5fa', '#2563eb', '#4338ca', '#7c3aed', '#9333ea'];

const checklist = [
  { title: 'Public profile scan', body: 'Aggregates commerce features, tech stack signals, and partnerships from public sources on arrival.' },
  { title: 'API & integration scoring', body: 'Rates API availability, auth & security, webhook support, headless readiness, and third-party integrations.' },
  { title: 'AI & automation scoring', body: 'Rates NLP/search, personalization, autonomous decisioning, conversational commerce, and data infrastructure.' },
  { title: 'UX & accessibility scoring', body: 'Rates checkout, multi-channel support, accessibility compliance, and agent-friendly interfaces.' },
  { title: 'Flags gaps instead of guessing', body: 'Where public evidence is inconclusive, the flow marks it unconfirmed; it never assumes a capability exists.' },
  { title: 'Tracks execution in a companion app', body: 'Turns the roadmap into a live Gantt chart, a 22-item checklist, and an AI-generated executive briefing.' },
];

const stamps = [
  { num: '01', title: 'Give it a company', body: 'Just a name or website, no API access or login needed' },
  { num: '02', title: 'The flow researches', body: 'Pulls public site data, tech signals, app listings, and press' },
  { num: '03', title: 'Scoring runs automatically', body: 'A documented rubric scores every dimension consistently' },
  { num: '04', title: 'Report delivered', body: 'Scorecard, radar chart, and roadmap, ready to share' },
];

const ledgerRows = [
  { label: 'Weighted domains: API, AI, UX', value: '3' },
  { label: 'Score with 4 readiness tiers', value: '0–100' },
  { label: 'Foundation → Leadership', value: '4 phases' },
  { label: 'Not days, name to report', value: 'Minutes' },
];

const AgenticReadinessOnePager = () => {
  return (
    <div className="w-full rounded-2xl bg-[#faf6ee] p-4 sm:p-6">
      <div
        className="relative mb-8 overflow-hidden rounded-md border border-[#e7dcc8] bg-[#fffdf8] p-6 pl-7 shadow-sm"
        style={{ borderLeft: `5px solid ${PALETTE.indigo}` }}
      >
        <div className="mb-4 text-xs font-bold uppercase tracking-widest" style={{ color: PALETTE.indigo }}>
          Business Case Summary
        </div>

        <div className="mb-4">
          <h4 className="text-xl text-gray-900">The Challenge</h4>
          <p className="mt-1 text-base italic text-gray-500">A slow, scattered audit that goes stale fast.</p>
          <p className="mt-2 text-base leading-relaxed text-gray-700">
            Retailers don&apos;t know where they stand on agentic commerce readiness. Answering that requires manually auditing tech stack signals, API exposure, checkout flows, accessibility posture, and conversational interfaces across dozens of scattered public sources: vendor case studies, app store listings, job postings, litigation records, press releases. The write-up is slow to produce, inconsistent from one company to the next, hard to benchmark, and stale within months as adoption accelerates.
          </p>
        </div>

        <hr className="my-4 border-dashed border-[#e7dcc8]" />

        <div>
          <h4 className="text-xl text-gray-900">The Outcome</h4>
          <p className="mt-1 text-base italic text-gray-500">One flow scores it, benchmarks it, and roadmaps it.</p>
          <p className="mt-2 text-base leading-relaxed text-gray-700">
            A Quick Flow that runs a structured, repeatable assessment of any retailer&apos;s public profile and technology footprint, scoring API readiness, AI &amp; automation maturity, and UX &amp; accessibility, and returns a single weighted score, a readiness tier, a visual breakdown of strengths and gaps, and a phased roadmap benchmarked against 2026 industry standards. No credentials, no internal access, no analyst week required.
          </p>
        </div>
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900">What the flow does</h3>
      <div className="mb-8 overflow-hidden rounded-md border border-[#e7dcc8] bg-[#fffdf8]">
        {checklist.map((item, i) => (
          <div
            key={item.title}
            className={`flex items-start gap-4 px-5 py-4 ${i !== checklist.length - 1 ? 'border-b border-[#f1e9d8]' : ''}`}
          >
            <svg
              className="mt-1.5 h-5 w-5 shrink-0"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke={PALETTE.indigo}
              strokeWidth="3"
              aria-hidden="true"
            >
              <path d="M20 6 9 17l-5-5" />
            </svg>
            <div>
              <h4 className="text-lg font-semibold text-gray-900">{item.title}</h4>
              <p className="mt-1 text-base text-gray-500">{item.body}</p>
            </div>
          </div>
        ))}
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900">
        Designed for business, not coders
      </h3>
      <div className="relative mb-8 rounded-md border border-[#e7dcc8] bg-[#fffdf8] p-7">
        <div
          className="absolute left-[6%] right-[6%] top-[59px] hidden h-[2px] sm:block"
          style={{ background: stampGradient, opacity: 0.35 }}
          aria-hidden="true"
        />
        <div className="relative grid grid-cols-1 gap-6 sm:grid-cols-4">
          {stamps.map((stamp, i) => (
            <div key={stamp.num} className="text-center">
              <div
                className="mx-auto mb-3 flex h-16 w-16 items-center justify-center rounded-full text-xl font-extrabold text-white"
                style={{
                  background: `linear-gradient(135deg, ${
                    [PALETTE.blueLight, PALETTE.blue, PALETTE.indigo, PALETTE.violet][i]
                  }, ${[PALETTE.blue, PALETTE.indigo, PALETTE.violet, PALETTE.violet][i]})`,
                }}
              >
                {stamp.num}
              </div>
              <h4 className="text-lg font-semibold text-gray-900">{stamp.title}</h4>
              <p className="mt-1 text-base text-gray-500">{stamp.body}</p>
            </div>
          ))}
        </div>
      </div>

      <h3 className="mb-4 border-b-2 border-gray-900 pb-2 text-2xl text-gray-900">
        Business Value Created
      </h3>
      <div className="grid grid-cols-1 rounded-md border border-[#e7dcc8] bg-[#fffdf8] sm:grid-cols-4">
        {ledgerRows.map((row, i) => (
          <div
            key={row.label}
            className={`flex flex-col items-center gap-1 px-4 py-5 text-center ${
              i !== ledgerRows.length - 1 ? 'border-b border-[#f1e9d8] sm:border-b-0 sm:border-r' : ''
            }`}
          >
            <span
              className="text-4xl font-extrabold"
              style={{
                background: `linear-gradient(135deg, ${LEDGER_COLORS[i]}, ${LEDGER_COLORS[i + 1]})`,
                WebkitBackgroundClip: 'text',
                backgroundClip: 'text',
                color: 'transparent',
              }}
            >
              {row.value}
            </span>
            <span className="text-base font-semibold text-gray-600">{row.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AgenticReadinessOnePager;
